import { BusinessObjectBase } from './base';
import { matchResultSchema } from '@schemas/business-objects/match-result';
import type { Match } from './match';
import type { FoalCount, Id, MatchAge } from '../base-types';

export interface MatchResultData {
  readonly id: Id;
  readonly age: MatchAge;
  readonly binUnder3?: FoalCount;
  readonly bin3To4?: FoalCount;
  readonly bin4To5?: FoalCount;
  readonly bin5To6?: FoalCount;
  readonly bin6To7?: FoalCount;
  readonly bin7To8?: FoalCount;
  readonly bin8Plus?: FoalCount;
  readonly averageBme?: number;
  readonly standardDeviation?: number;
  readonly mlfScore?: number;
  readonly phenotype?: string;
  readonly ringOneUnder3?: FoalCount;
  readonly ringOne3To4?: FoalCount;
  readonly ringOne4To5?: FoalCount;
  readonly ringOne5To6?: FoalCount;
  readonly ringOne6To7?: FoalCount;
  readonly ringOne7To8?: FoalCount;
  readonly ringOne8Plus?: FoalCount;
  readonly ringTwoUnder3?: FoalCount;
  readonly ringTwo3To4?: FoalCount;
  readonly ringTwo4To5?: FoalCount;
  readonly ringTwo5To6?: FoalCount;
  readonly ringTwo6To7?: FoalCount;
  readonly ringTwo7To8?: FoalCount;
  readonly ringTwo8Plus?: FoalCount;
  readonly ringThreeUnder3?: FoalCount;
  readonly ringThree3To4?: FoalCount;
  readonly ringThree4To5?: FoalCount;
  readonly ringThree5To6?: FoalCount;
  readonly ringThree6To7?: FoalCount;
  readonly ringThree7To8?: FoalCount;
  readonly ringThree8Plus?: FoalCount;
  readonly legacyId?: number;
  readonly matchId: Id;
  readonly match?: Match;
}

export interface MatchResult extends Readonly<MatchResultData> {}
type MatchResultCreateWithChildrenData = Partial<MatchResultData>;

export class MatchResult extends BusinessObjectBase {
  static readonly entityName = 'MatchResult' as const;
  static readonly plural = 'match-results';
  static schema = matchResultSchema;

  // --- Static CRUD methods ---

  static async find(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<MatchResult[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params, undefined, options);
    return results.map((data) => new MatchResult(data));
  }

  static async findOne(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<MatchResult | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return data ? new MatchResult(data) : null;
  }

  static async findById(
    id: Id,
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<MatchResult> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return new MatchResult(data);
  }

  static async create(data: Omit<MatchResultData, 'id'>): Promise<MatchResult> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new MatchResult(result);
  }

  static async createMany(
    data: Omit<MatchResultData, 'id'>[],
  ): Promise<MatchResult[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new MatchResult(item));
  }

  static async createWithChildren(
    data: MatchResultCreateWithChildrenData,
  ): Promise<MatchResult> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new MatchResult(result);
  }

  static async createManyWithChildren(
    data: MatchResultCreateWithChildrenData[],
  ): Promise<MatchResult[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new MatchResult(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<MatchResultData>,
  ): Promise<MatchResult> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new MatchResult(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
